import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { UserService } from 'app/core/user/user.service';
import { environment } from 'environments/environment';
import Keycloak, { KeycloakProfile } from 'keycloak-js';
import { BehaviorSubject, Observable } from 'rxjs';
import { User } from '../user/user.types';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly _keycloak = inject(Keycloak);
  private readonly isLoggedInSubject = new BehaviorSubject<boolean>(false);
  private readonly userBehaviorSubject = new BehaviorSubject<User>(Object.assign({}));
  userBehaviorSubject$ = this.userBehaviorSubject.asObservable();
  private readonly _userService = inject(UserService);
  private readonly _httpClient = inject(HttpClient);
  // Dedupes loadUserProfile() calls triggered lazily from getUserRoles()
  private _profileRequest: Promise<void> | null = null;


  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  forgotPassword(email: string): Observable<unknown> {
    return this._httpClient.post('api/auth/forgot-password', email);
  }

  resetPassword(password: string): Observable<unknown> {
    return this._httpClient.post('api/auth/reset-password', password);
  }

  signIn(): void {
    this._keycloak.login();
  }

  signOut(): void {
    this._keycloak.logout({ redirectUri: environment.keyCloak.postLogoutRedirectUri });
  }

  signUp(user: {
    name: string;
    email: string;
    password: string;
    company: string;
  }): Observable<unknown> {
    return this._httpClient.post('api/auth/sign-up', user);
  }

  unlockSession(credentials: { email: string; password: string }): Observable<unknown> {
    return this._httpClient.post('api/auth/unlock-session', credentials);
  }

  getUserInformation(): Promise<KeycloakProfile> {
    return this._keycloak.loadUserProfile();
  }

  isLoggedIn(): Observable<boolean> {
    this.isLoggedInSubject.next(!!this._keycloak.authenticated);
    return this.isLoggedInSubject.asObservable();
  }

  getUsername(): string {
    return this._keycloak.profile?.username ?? '';
  }

  getUserEmail(): string {
    return this._keycloak.profile?.email ?? '';
  }

  getUserKeycloakId(): string | undefined {
    return this._keycloak.profile?.id;
  }

  /**
   * Asynchronously checks if the user has any of the specified roles.
   */
  async roleMatch(allowedRoles: string[]): Promise<boolean> {
    const userProfile = await this._keycloak.loadUserProfile();
    const academyRole = (userProfile.attributes?.academy_spa_role as string[]) ?? [];
    return allowedRoles.some(role => academyRole.includes(role));
  }

  /**
   * Returns an array of user roles extracted from the academy_spa_role attribute.
   * `keycloak.profile` is only set once loadUserProfile() resolves, so the first call
   * (before that happens) triggers the load in the background and returns [] meanwhile;
   * callers that retry (e.g. app.component's role-wait loop) will see it once cached.
   */
  getUserRoles(): string[] {
    const roles = this._keycloak.profile?.attributes?.academy_spa_role as string[] | undefined;
    if (roles?.length) {
      return roles;
    }
    this._ensureProfileLoaded();
    return [];
  }

  /**
   * Loads the profile once, caching it on `keycloak.profile` for later reads. Never
   * throws: a rejection (expired token, network error) just clears the in-flight flag
   * so a later call can retry.
   */
  private _ensureProfileLoaded(): void {
    if (this._profileRequest) {
      return;
    }
    this._profileRequest = this._keycloak
      .loadUserProfile()
      .then(() => undefined)
      .catch(() => undefined)
      .finally(() => {
        this._profileRequest = null;
      });
  }
}
